import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	getFamilyStories,
	saveFamilyStory,
	deleteFamilyStory,
	addFamilyStoryPhoto,
	deleteFamilyStoryPhoto,
	reorderFamilyStoryPhotos,
} from "../../api/apiServiceFetch";
import { getFileBaseUrl } from "../../config/environment";
import PhotoCropper from "./PhotoCropper";
import "./FamilyStories.scss";

const LANGS = [
	{ value: "ru", label: "Русский" },
	{ value: "en", label: "Английский" },
	{ value: "hy", label: "Армянский" },
];

const emptyText = (lang) => ({
	lang,
	names: "",
	meta: "",
	summary: "",
	body: "",
});

const emptyStory = () => ({
	id: null,
	sortOrder: 0,
	isPublished: false,
	texts: LANGS.map((l) => emptyText(l.value)),
	photos: [],
});

/** Приводит ответ сервера к форме, в которой все языки всегда присутствуют. */
const toForm = (story) => ({
	id: story.id,
	sortOrder: story.sortOrder ?? 0,
	isPublished: !!story.isPublished,
	photos: story.photos ?? [],
	texts: LANGS.map(
		(l) => story.texts?.find((t) => t.lang === l.value) ?? emptyText(l.value),
	),
});

// Кадрирование отдаёт JPEG, каким бы ни был исходник.
const jpgName = (name) => `${name.replace(/\.[^.]+$/, "")}.jpg`;

const readAsBase64 = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = String(reader.result || "");
			const comma = result.indexOf(",");
			resolve(comma >= 0 ? result.slice(comma + 1) : result);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});

const FamilyStories = () => {
	const [stories, setStories] = useState([]);
	const [form, setForm] = useState(null);
	const [lang, setLang] = useState("ru");
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [status, setStatus] = useState(null);
	const [cropQueue, setCropQueue] = useState([]);
	const [cropTotal, setCropTotal] = useState(0);
	const fileRef = useRef(null);

	const filePath = getFileBaseUrl();

	const photoUrl = useCallback(
		(storyId, fileName) => `${filePath}/FamilyStory/${storyId}/${fileName}`,
		[filePath],
	);

	const reload = useCallback(async (selectId) => {
		setIsLoading(true);
		try {
			const items = await getFamilyStories();
			setStories(items);

			if (selectId) {
				const found = items.find((s) => s.id === selectId);
				if (found) setForm(toForm(found));
			}
		} catch (error) {
			console.error("Load family stories error:", error);
			setStatus({ ok: false, text: "Не удалось загрузить истории" });
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		reload();
	}, [reload]);

	const currentText = useMemo(
		() => form?.texts.find((t) => t.lang === lang) ?? emptyText(lang),
		[form, lang],
	);

	const setField = (field, value) => {
		setForm((prev) => ({
			...prev,
			texts: prev.texts.map((t) =>
				t.lang === lang ? { ...t, [field]: value } : t,
			),
		}));
	};

	const onSave = async () => {
		if (!form) return;

		// Без имён история не покажется ни на одном языке — это единственное
		// обязательное поле.
		if (!form.texts.some((t) => t.names.trim())) {
			setStatus({ ok: false, text: "Заполните имена хотя бы на одном языке" });
			return;
		}

		setIsSaving(true);
		setStatus(null);
		try {
			const saved = await saveFamilyStory({
				id: form.id ?? undefined,
				sortOrder: Number(form.sortOrder) || 0,
				isPublished: form.isPublished,
				texts: form.texts,
			});

			setStatus({ ok: true, text: "Сохранено" });
			await reload(saved?.id ?? form.id);
		} catch (error) {
			console.error("Save family story error:", error);
			setStatus({ ok: false, text: "Не удалось сохранить" });
		} finally {
			setIsSaving(false);
		}
	};

	const onDelete = async () => {
		if (!form?.id) return;
		if (!window.confirm("Удалить историю?")) return;

		try {
			await deleteFamilyStory(form.id);
			setForm(null);
			await reload();
		} catch (error) {
			console.error("Delete family story error:", error);
			setStatus({ ok: false, text: "Не удалось удалить" });
		}
	};

	const onPickPhotos = (event) => {
		const files = Array.from(event.target.files || []);
		event.target.value = "";

		if (!files.length) return;

		if (!form?.id) {
			// Фотографии привязываются к записи, поэтому историю надо сперва
			// сохранить — иначе некуда их класть.
			setStatus({ ok: false, text: "Сначала сохраните историю" });
			return;
		}

		setCropTotal(files.length);
		setCropQueue(files);
	};

	const uploadFromQueue = async (fileName, base64, miniBase64 = null) => {
		setIsSaving(true);
		try {
			await addFamilyStoryPhoto(form.id, fileName, base64, miniBase64);
			// Снимаем из очереди только после успеха — иначе кадрирование
			// пропало бы вместе с файлом.
			setCropQueue((queue) => queue.slice(1));
			await reload(form.id);
			setStatus({ ok: true, text: "Фотография добавлена" });
		} catch (error) {
			console.error("Add family story photo error:", error);
			setStatus({ ok: false, text: "Не удалось загрузить фотографию" });
		} finally {
			setIsSaving(false);
		}
	};

	// Двойной crop: первым кадром режем «на весь экран телефона» (9:16), вторым —
	// обложку для карточки (327×138). Между шагами держим промежуточный
	// phoneBase64; на втором подтверждении уходит оба крана в API.
	const onCropConfirm = (phoneBase64, cardBase64) =>
		uploadFromQueue(jpgName(cropQueue[0].name), phoneBase64, cardBase64);

	const onUploadOriginal = async () => {
		const file = cropQueue[0];
		const base64 = await readAsBase64(file);
		// Без ручного crop — обе дорожки берутся из одного и того же файла, мини
		// сгенерирует бэкенд (как раньше).
		await uploadFromQueue(file.name, base64, null);
	};

	const onCropSkip = () => setCropQueue((queue) => queue.slice(1));

	const onDeletePhoto = async (photoId) => {
		if (!window.confirm("Удалить фотографию?")) return;

		try {
			await deleteFamilyStoryPhoto(photoId);
			await reload(form.id);
		} catch (error) {
			console.error("Delete family story photo error:", error);
			setStatus({ ok: false, text: "Не удалось удалить фотографию" });
		}
	};

	const movePhoto = async (index, delta) => {
		const photos = [...(form?.photos ?? [])];
		const target = index + delta;
		if (target < 0 || target >= photos.length) return;

		[photos[index], photos[target]] = [photos[target], photos[index]];

		try {
			await reorderFamilyStoryPhotos(form.id, photos.map((p) => p.id));
			await reload(form.id);
		} catch (error) {
			console.error("Reorder family story photos error:", error);
			setStatus({ ok: false, text: "Не удалось изменить порядок" });
		}
	};

	return (
		<div className="fsa">
			<h2 className="content-block">Семьи, созданные в Jerm</h2>

			<div className="fsa__layout">
				<aside className="fsa__list dx-card">
					<button
						type="button"
						className="fsa__btn fsa__btn--primary fsa__add"
						onClick={() => {
							setForm(emptyStory());
							setLang("ru");
							setStatus(null);
						}}
					>
						Добавить историю
					</button>

					{isLoading && <div className="fsa__hint">Загрузка…</div>}

					{!isLoading && stories.length === 0 && (
						<div className="fsa__hint">Историй пока нет</div>
					)}

					{stories.map((story) => {
						const title =
							story.texts?.find((t) => t.lang === "ru")?.names ||
							story.texts?.[0]?.names ||
							`Без названия #${story.id}`;

						return (
							<button
								key={story.id}
								type="button"
								className={`fsa__item ${form?.id === story.id ? "isActive" : ""}`}
								onClick={() => {
									setForm(toForm(story));
									setStatus(null);
								}}
							>
								<span className="fsa__itemName">{title}</span>
								<span className="fsa__itemMeta">
									{story.isPublished ? "опубликована" : "черновик"} ·{" "}
									{story.photos?.length ?? 0} фото · порядок {story.sortOrder}
								</span>
							</button>
						);
					})}
				</aside>

				<section className="fsa__editor dx-card">
					{!form && <div className="fsa__hint">Выберите историю слева</div>}

					{form && (
						<>
							<div className="fsa__row">
								<label className="fsa__field fsa__field--narrow">
									<span>Порядок</span>
									<input
										type="number"
										value={form.sortOrder}
										onChange={(e) =>
											setForm({ ...form, sortOrder: e.target.value })
										}
									/>
								</label>

								<label className="fsa__check">
									<input
										type="checkbox"
										checked={form.isPublished}
										onChange={(e) =>
											setForm({ ...form, isPublished: e.target.checked })
										}
									/>
									<span>Опубликована</span>
								</label>
							</div>

							<div className="fsa__tabs">
								{LANGS.map((l) => {
									const filled = form.texts.find(
										(t) => t.lang === l.value,
									)?.names;

									return (
										<button
											key={l.value}
											type="button"
											className={`fsa__tab ${lang === l.value ? "isActive" : ""}`}
											onClick={() => setLang(l.value)}
										>
											{l.label}
											{!filled && <span className="fsa__tabDot" title="пусто" />}
										</button>
									);
								})}
							</div>

							<label className="fsa__field">
								<span>Имена пары</span>
								<input
									value={currentText.names}
									onChange={(e) => setField("names", e.target.value)}
									placeholder="Армен и Анна"
								/>
							</label>

							<label className="fsa__field">
								<span>Города и возраст</span>
								<input
									value={currentText.meta}
									onChange={(e) => setField("meta", e.target.value)}
									placeholder="Ереван и Москва · 28 и 27 лет"
								/>
							</label>

							<label className="fsa__field">
								<span>Анонс для карточки</span>
								<textarea
									rows={3}
									value={currentText.summary}
									onChange={(e) => setField("summary", e.target.value)}
								/>
							</label>

							<label className="fsa__field">
								<span>Текст истории (пустая строка разделяет абзацы)</span>
								<textarea
									rows={12}
									value={currentText.body}
									onChange={(e) => setField("body", e.target.value)}
								/>
							</label>

							<div className="fsa__photos">
								<div className="fsa__photosHead">
									<span>Фотографии</span>
									<button
										type="button"
										className="fsa__btn"
										onClick={() => fileRef.current?.click()}
										disabled={!form.id || isSaving}
										title={form.id ? "" : "Сначала сохраните историю"}
									>
										Добавить
									</button>
									<input
										ref={fileRef}
										type="file"
										accept="image/*"
										multiple
										hidden
										onChange={onPickPhotos}
									/>
								</div>

								<div className="fsa__grid">
									{form.photos.map((photo, i) => (
										<div key={photo.id} className="fsa__photo">
											<img
												src={photoUrl(form.id, photo.miniFileName || photo.fileName)}
												alt=""
											/>
											{i === 0 && <span className="fsa__cover">обложка</span>}
											<div className="fsa__photoBtns">
												<button type="button" onClick={() => movePhoto(i, -1)}>
													←
												</button>
												<button type="button" onClick={() => movePhoto(i, 1)}>
													→
												</button>
												<button
													type="button"
													onClick={() => onDeletePhoto(photo.id)}
												>
													✕
												</button>
											</div>
										</div>
									))}

									{form.photos.length === 0 && (
										<div className="fsa__hint">Фотографий нет</div>
									)}
								</div>
							</div>

							<div className="fsa__actions">
								<button
									type="button"
									className="fsa__btn fsa__btn--primary"
									onClick={onSave}
									disabled={isSaving}
								>
									{isSaving ? "Сохранение…" : "Сохранить"}
								</button>

								{form.id && (
									<button
										type="button"
										className="fsa__btn fsa__btn--danger"
										onClick={onDelete}
										disabled={isSaving}
									>
										Удалить
									</button>
								)}

								{status && (
									<span className={`fsa__status ${status.ok ? "isOk" : "isFail"}`}>
										{status.text}
									</span>
								)}
							</div>
						</>
					)}
				</section>
			</div>

			{cropQueue.length > 0 && (
				<PhotoCropper
					key={`${cropQueue[0].name}-${cropQueue.length}`}
					file={cropQueue[0]}
					position={cropTotal - cropQueue.length + 1}
					total={cropTotal}
					onConfirm={onCropConfirm}
					onUploadOriginal={onUploadOriginal}
					onSkip={onCropSkip}
					onCancelAll={() => setCropQueue([])}
				/>
			)}
		</div>
	);
};

export default FamilyStories;
